import {
  useLazyGetCashBreakdownQuery,
  usePostCashBreakdownMutation,
} from '@api/front-office/front-office.api.rtk';
import { AmericanExpressIcon } from '@assets/icons/cash-breakdown/bank/AmericanExpressIcon';
import { DiscoverIcon } from '@assets/icons/cash-breakdown/bank/DiscoverIcon';
import { HSBCIcon } from '@assets/icons/cash-breakdown/bank/HSBCIcon';
import { JCBIcon } from '@assets/icons/cash-breakdown/bank/JCBICon';
import { MasterCardIcon } from '@assets/icons/cash-breakdown/bank/MasterCardIcon';
import { VisaCardIcon } from '@assets/icons/cash-breakdown/bank/VisaIcon';
import { ApplePayIcon } from '@assets/icons/cash-breakdown/epayment/ApplePayIcon';
import { GCashIcon } from '@assets/icons/cash-breakdown/epayment/GCashIcon';
import { GooglePayIcon } from '@assets/icons/cash-breakdown/epayment/GooglePayIcon';
import { MayaIcon } from '@assets/icons/cash-breakdown/epayment/MayaIcon';
import { PayPalIcon } from '@assets/icons/cash-breakdown/epayment/PayPalIcon';
import { getColor } from '@assets/theme';
import DrawerWrapper from '@components/drawer/drawer-wrapper';
import ControlledTextField from '@components/form/controlled/controlled-textfield';
import DataTable from '@components/table/datatable';
import { RenderTextFieldEditInputCell } from '@components/table/datatable-edit';
import useControlledDatatable from '@hooks/datatable/useControlledDatatable';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import useCreateContext from '@hooks/useCreateContext';
import useDisclosure, { UseDisclosureInterface } from '@hooks/useDisclosure';
import useToast from '@hooks/useToast';
import useUser from '@hooks/useUser';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from '@store/index';
import {
  IconCash,
  IconCoins,
  IconCreditCard,
  IconDevicesDollar,
  IconRefresh,
} from '@tabler/icons-react';
import { dateFormat, formatNumber, toReportInputs } from '@utils/format';
import { handleCamelCase } from '@utils/keypress';
import { SHA512 } from 'crypto-js';
import moment from 'moment';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ICashBreakdown,
  ICashBreakdownList,
  ICashBreakdownObject,
} from '../interface/fo-cash-breakdown.interface';
import {
  usePostPOSDeclareNewDay,
  usePutEndShift,
} from '@api/pos/pos-work-period.api.rtk';
import { useLazyGetTemplateQuery } from '@api/templates.api.rtk';
import FormPDF, { IFormPDFRef } from '@components/pdf/form-pdf';
import useAppContext from '@hooks/useAppContext';
import { clearPOSMasterlist } from '@modules/point-of-sale/slice/pos-masterlist.slice';
import {
  selectPOSPrinter,
  selectPOSShiftID,
  selectPOSstationID,
  selectPOSsubModuleCode,
} from '@modules/point-of-sale/slice/pos-selector';
import { clearPOSStation } from '@modules/point-of-sale/slice/pos-stations.slice';
import { clearPOSTableArea } from '@modules/point-of-sale/slice/pos-table-area.slice';
import { formatAddress } from '@store/slices/property.slice';
import { useLazyGetFilesQuery } from '@api/files.api.rtk';
import useS3Bucket from '@hooks/useS3Bucket';
import { hashSignature } from '@utils/security';
import { selectSalesDateByPath } from '@store/selector/app.selector';
import { useCurrentModule } from '@hooks/useCurrentModule';

interface IDataTable {
  bills: ICBBillsCoins[];
  coins: ICBBillsCoins[];
  card: ICBCard[];
  ePayment: ICBCard[];
  others: ICBCard[];
}

interface ITotal {
  cashTotal: number;
  billsTotal: number;
  coinsTotal: number;
  cardTotal: number;
  checkTotal: number;
  giftChecksTotal: number;
  ePaymentTotal: number;
  othersTotal: number;
  grandTotal: number;
}

interface IFOCashBreakdown {
  dataTableState: [
    IDataTable,
    React.Dispatch<React.SetStateAction<IDataTable>>,
  ];
  totalState: [ITotal, React.Dispatch<React.SetStateAction<ITotal>>];
}

const { CtxProvider, useCtx } =
  useCreateContext<IFOCashBreakdown>('FOCashBreakDown');

const useFoCashBreakdownCtx = () => useCtx();

// #region PDF REPORT
const CashBreakdownPDF = ({
  refId,
  modalDisclosure,
}: {
  refId: string;
  modalDisclosure: UseDisclosureInterface;
}) => {
  const property = useAppSelector((state) => state.properties.properties);
  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );
  const lastGeneratedId = useRef<string | null>(null);
  const formRef = useRef<IFormPDFRef>(null);
  const { toast } = useToast();
  const { getFile } = useS3Bucket();

  const [getFiles] = useLazyGetFilesQuery();
  const [getTemplate] = useLazyGetTemplateQuery();
  const [getCashBreakdown] = useLazyGetCashBreakdownQuery();

  const { startLoading, stopLoading } = useAppContext();

  useEffect(() => {
    const fetchAndGeneratePDF = async () => {
      if (
        !modalDisclosure.isOpen ||
        !refId ||
        lastGeneratedId.current === refId
      )
        return;

      startLoading();
      try {
        const data = await getCashBreakdown(refId).unwrap();
        if (data) {
          await handleGeneratePDF(data);
          lastGeneratedId.current = refId;
        }
      } catch (error) {
        console.error('Error generating PDF:', error);
      } finally {
        stopLoading();
      }
    };

    fetchAndGeneratePDF();
  }, [refId, modalDisclosure.isOpen]);

  const handleGeneratePDF = async (data: ICashBreakdownList) => {
    const template = await getTemplate({
      department: 'FO',
      category: 'Reservations',
      templateName: 'Cash Breakdown',
    }).unwrap();

    if (!template) {
      toast({ message: 'No Template Found', severity: 'warning' });
      return;
    }

    const propertyDetails = property?.find(
      (item) => item.propertyID === data?.propertyID,
    );

    const {
      bills = [],
      coins = [],
      cards = [],
      ePayments = [],
      others = [],
      totalBillAmount,
      totalCoinsAmount,
      totalCardAmount,
      totalEPaymentsAmount,
      totalOthersAmount,
      totalCollectionsSubmitted,
      totalCollectionsPosted,
      shortOver,
      userShortName,
      cutOffDate,
      dntCreated,
      remarks,
    } = data || {};

    const { propertyName, address } = propertyDetails || {};

    const files = await getFiles(selectedProperty?.value!).unwrap();
    let propertyLogo: string | null = null;

    if (files?.length > 0) {
      const fileId = files?.find(
        (file) => file.descr === 'Property Logo',
      )?.fileID;
      if (fileId) {
        propertyLogo = await getFile(fileId);
      }
    }

    // Helper function to get count and amount
    const getCount = (list: any[], key: string) =>
      list?.find((item) => item[key])?.[key] || 0;
    const getAmount = (list: any[], key: string) =>
      formatNumber(list?.find((item) => item[key])?.[key] || 0, 2, 2);

    const pdfInputs = [
      {
        propertyName: propertyName || '-',
        propertyDetails: address ? formatAddress(address) : '-',
        userFieldValue: userShortName,
        cutoffDate: moment(cutOffDate).format(dateFormat),
        dateAndTimeIssued: moment(dntCreated).format('MMM DD, YYYY hh:mm A'),
        remarksDetail: remarks,
        ...(propertyLogo && { propertyLogo }),
        moduleVersion: `Xenia FOS ${APP_VERSION}S`,

        // Bills
        d1k: getCount(bills, 'countBill1000'),
        s1k: getAmount(bills, 'amountBill1000'),
        d500: getAmount(bills, 'amountBill500'),
        s500: getAmount(bills, 'amountBill500'),
        d200: getAmount(bills, 'amountBill200'),
        s200: getAmount(bills, 'amountBill200'),
        d100: getAmount(bills, 'amountBill100'),
        s100: getAmount(bills, 'amountBill100'),
        d50: getAmount(bills, 'amountBill50'),
        s50: getAmount(bills, 'amountBill50'),
        d20: getCount(bills, 'countBill20') + getCount(coins, 'countCoins20'),
        s20: formatNumber(
          parseFloat(getAmount(bills, 'amountBill20')) +
            parseFloat(getAmount(coins, 'amountCoins20')),
          2,
          2,
        ),

        // Coins
        d10: getAmount(coins, 'amountCoins10'),
        s10: getAmount(coins, 'amountCoins10'),
        d5: getAmount(coins, 'amountCoins5'),
        s5: getAmount(coins, 'amountCoins5'),
        d1: getAmount(coins, 'amountCoins1'),
        s1: getAmount(coins, 'amountCoins1'),
        dP25: getAmount(coins, 'amountCoins25c'),
        sP25: getAmount(coins, 'amountCoins25c'),
        dP10: getAmount(coins, 'amountCoins10c'),
        sP10: getAmount(coins, 'amountCoins10c'),
        dP05: getAmount(coins, 'amountCoins05c'),
        sP05: getAmount(coins, 'amountCoins05c'),
        dP01: getAmount(coins, 'amountCoins01c'),
        sP01: getAmount(coins, 'amountCoins01c'),

        // Cards
        sMasterCard: getAmount(cards, 'totalCardMastercard'),
        sVisa: getAmount(cards, 'totalCardVisa'),
        sJCB: getAmount(cards, 'totalCardJCB'),
        sHSBC: getAmount(cards, 'totalCardHSBC'),
        sDiscover: getAmount(cards, 'totalCardDiscover'),
        sAmericanExpress: getAmount(cards, 'totalCardAmEx'),
        sOtherCards: getAmount(cards, 'totalCardOthers'),

        // E-Payment
        sPaypal: getAmount(ePayments, 'totalEPaymentPayPal'),
        sGCash: getAmount(ePayments, 'totalEPaymentGCash'),
        sMaya: getAmount(ePayments, 'totalEPaymentMaya'),
        sApplePay: getAmount(ePayments, 'totalEPaymentApple'),
        sGooglePay: getAmount(ePayments, 'totalEPaymentGoogle'),
        sOtherEPayments: getAmount(ePayments, 'totalEPaymentOthers'),

        // Other Payment Methods
        sCheques: getAmount(others, 'totalOthersCheques'),
        sGiftChecks: getAmount(others, 'totalOthersGiftCertificates'),
        sVouchers: getAmount(others, 'totalOthersVouchers'),
        sOtherPayments: getAmount(others, 'totalOthersOthers'),

        // Totals
        totalCash: formatNumber(
          parseFloat(totalBillAmount || '0') +
            parseFloat(totalCoinsAmount || '0'),
          2,
          2,
        ),
        totalCard: formatNumber(parseFloat(totalCardAmount || '0'), 2, 2),
        totalEPayment: formatNumber(
          parseFloat(totalEPaymentsAmount || '0'),
          2,
          2,
        ),
        totalOthers: formatNumber(parseFloat(totalOthersAmount || '0'), 2, 2),
        totalSubmittedPayments: formatNumber(
          parseFloat(totalCollectionsSubmitted || '0'),
          2,
          2,
        ),
        totalActualPayments: formatNumber(
          parseFloat(totalCollectionsPosted || '0'),
          2,
          2,
        ),
        shortOver: formatNumber(parseFloat(shortOver || '0'), 2, 2),
        remarks,
      },
    ];

    formRef.current?.handleShowPDF({
      template,
      inputs: { ...toReportInputs(pdfInputs[0]) },
    });
  };

  return (
    <FormPDF
      {...{
        ref: formRef,
        title: 'Cash Breakdown Report',
        modalOnClose: modalDisclosure.onClose,
      }}
    />
  );
};
// #endregion

// #region TRANSACTION CARD
const TransactionCard = memo(
  ({
    icon,
    title,
    amount,
    isActive,
    onClick,
  }: {
    icon: JSX.Element;
    title: string;
    amount: number;
    isActive: boolean;
    onClick: (data: string) => void;
  }) => {
    const { primary, white, textSecondary } = getColor();
    return (
      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
          p: 2,
          gap: 2,
          bgcolor: isActive ? `${primary}24` : white,
          borderRight: '3px solid',
          borderColor: isActive ? primary : 'transparent',
          cursor: isActive ? 'default' : 'pointer',
        }}
        onClick={isActive ? undefined : () => onClick(title)}
      >
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            bgcolor: isActive ? primary : '#757575',
            borderRadius: '100%',
          }}
        >
          {icon}
        </Stack>
        <Stack>
          <Typography sx={{ color: isActive ? primary : textSecondary }}>
            {title}
          </Typography>
          <Typography>{formatNumber(amount, 2, 2)}</Typography>
        </Stack>
      </Stack>
    );
  },
);

const transactions: { icon: JSX.Element; title: string }[] = [
  { icon: <IconCash size={24} color='#FFF' />, title: 'Bills' },
  { icon: <IconCoins size={24} color='#FFF' />, title: 'Coins' },
  {
    icon: <IconCreditCard size={24} color='#FFF' />,
    title: 'Credit/Debit Card',
  },
  { icon: <IconDevicesDollar size={24} color='#FFF' />, title: 'E-Payment' },
  { icon: <IconRefresh size={24} color='#FFF' />, title: 'Others' },
];
// #endregion

// #region TOTAL CARD
const TotalCard = ({ title, amount }: { title: string; amount: number }) => {
  const { borderBottom, textSecondary } = getColor();
  return (
    <Stack sx={{ py: 1, px: 2, borderBottom: `1px dashed ${borderBottom}` }}>
      <Typography sx={{ color: textSecondary }}>{title}</Typography>
      <Typography sx={{ textAlign: 'right' }}>
        {formatNumber(amount, 2, 2)}
      </Typography>
    </Stack>
  );
};

const totalCards: { title: string; amount: number }[] = [
  { title: 'Cash Total', amount: 0 },
  { title: 'Card Total', amount: 0 },
  { title: 'Check Total', amount: 0 },
  { title: 'Gift Checks Total', amount: 0 },
  { title: 'Others Total', amount: 0 },
];
// #endregion

// #region BILLS & COINS
interface ICBBillsCoins {
  referenceID: string;
  pieces: number;
  denomination: number;
  subTotal: number;
  type: string;
}

const BillsCoinsColumns: GridColDef[] = [
  {
    field: 'pieces',
    headerName: 'Pieces',
    minWidth: 100,
    editable: true,
    cellClassName: 'editable',
    flex: 1,
    type: 'number',
    renderEditCell: (params) => {
      return (
        <RenderTextFieldEditInputCell
          {...params}
          sx={{ '& .MuiInputBase-input': { textAlign: 'center' } }}
          type='number'
          inputProps={{ min: 0 }}
          onFocus={() => {
            if (params.value === 0) {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: undefined,
              });
            }
          }}
          onBlur={(event) => {
            if (!event.target.value) {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: 0,
              });
            }
          }}
          onKeyDown={(event) => {
            const input = event.target as HTMLInputElement;
            if (event.key === 'Enter' && !input.value) {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: 0,
              });
            }
          }}
        />
      );
    },
    align: 'center',
    headerAlign: 'center',
    hideSortIcons: true,
    sortable: false,
  },
  {
    field: 'denomination',
    headerName: 'Denomination',
    minWidth: 100,
    flex: 1,
    hideSortIcons: true,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell(params) {
      return formatNumber(params.value, 2, 2);
    },
  },
  {
    field: 'subTotal',
    headerName: 'Sub Total',
    minWidth: 100,
    flex: 1,
    hideSortIcons: true,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell(params) {
      return formatNumber(params.value, 2, 2);
    },
  },
];

const billsInitialData: ICBBillsCoins[] = [
  {
    referenceID: '1',
    pieces: 0,
    denomination: 1000,
    subTotal: 0,
    type: 'bills',
  },
  {
    referenceID: '2',
    pieces: 0,
    denomination: 500,
    subTotal: 0,
    type: 'bills',
  },
  {
    referenceID: '3',
    pieces: 0,
    denomination: 200,
    subTotal: 0,
    type: 'bills',
  },
  {
    referenceID: '4',
    pieces: 0,
    denomination: 100,
    subTotal: 0,
    type: 'bills',
  },
  { referenceID: '5', pieces: 0, denomination: 50, subTotal: 0, type: 'bills' },
  { referenceID: '6', pieces: 0, denomination: 20, subTotal: 0, type: 'bills' },
];

const coinsInitialData: ICBBillsCoins[] = [
  { referenceID: '1', pieces: 0, denomination: 20, subTotal: 0, type: 'coins' },
  { referenceID: '2', pieces: 0, denomination: 10, subTotal: 0, type: 'coins' },
  { referenceID: '3', pieces: 0, denomination: 5, subTotal: 0, type: 'coins' },
  { referenceID: '4', pieces: 0, denomination: 1, subTotal: 0, type: 'coins' },
  {
    referenceID: '5',
    pieces: 0,
    denomination: 0.25,
    subTotal: 0,
    type: 'coins',
  },
  {
    referenceID: '6',
    pieces: 0,
    denomination: 0.1,
    subTotal: 0,
    type: 'coins',
  },
  {
    referenceID: '7',
    pieces: 0,
    denomination: 0.05,
    subTotal: 0,
    type: 'coins',
  },
  {
    referenceID: '8',
    pieces: 0,
    denomination: 0.01,
    subTotal: 0,
    type: 'coins',
  },
];

const Bills = () => {
  const { dataTableState, totalState } = useFoCashBreakdownCtx();
  const [dataTable, setDataTable] = dataTableState;
  const [_, setTotal] = totalState;

  const processRow = (row: ICBBillsCoins) => {
    const subTotal = row.denomination * row.pieces;
    return { ...row, subTotal };
  };

  const {
    // HANDLE EDIT
    datatableData,
    rowEdit,
  } = useControlledDatatable('cash-breakdown-bills', {
    isOpen: true,
    primaryKey: 'denomination',
    processRow,
    initialData: billsInitialData.map((item) => ({
      ...item,
      subTotal:
        dataTable.bills.find((card) => card.denomination === item.denomination)
          ?.subTotal || 0,
      pieces:
        dataTable.bills.find((card) => card.denomination === item.denomination)
          ?.pieces || 0,
    })) as ICBBillsCoins[],
    defaultClassName: true,
  });

  useEffect(() => {
    setDataTable((prev) => ({ ...prev, bills: datatableData }));
  }, [datatableData]);

  useEffect(() => {
    const cashTotal =
      dataTable.bills.reduce((sum, item) => sum + item.subTotal, 0) +
      dataTable.coins.reduce((sum, item) => sum + item.subTotal, 0);

    setTotal((prev) => ({
      ...prev,
      cashTotal,
      billsTotal: dataTable.bills.reduce((sum, item) => sum + item.subTotal, 0),
    }));
  }, [dataTable]);

  return (
    <Stack
      sx={{
        flex: 1,
        px: 1,
        '& .MuiDataGrid-columnHeaders': {
          maxHeight: '36px!important',
          minHeight: '36px!important',
        },
      }}
    >
      <DataTable
        // CONFIG
        getRowId={(row) => row.referenceID}
        rows={datatableData}
        columns={BillsCoinsColumns}
        pageSize={100}
        autoPageSize={false}
        checkboxSelection={false}
        tableStyle={{ p: 0 }}
        disableAdditionalLoading
        disableColumnFilter
        disableSearch
        fullHeight
        hideFooter
        disableColumnMenu
        disableColumnSelector
        {...rowEdit}
      />
    </Stack>
  );
};

const Coins = () => {
  const { dataTableState, totalState } = useFoCashBreakdownCtx();
  const [dataTable, setDataTable] = dataTableState;
  const [_, setTotal] = totalState;

  const processRow = (row: ICBBillsCoins) => {
    const subTotal = row.denomination * row.pieces;
    return { ...row, subTotal };
  };

  const {
    // HANDLE EDIT
    datatableData,
    rowEdit,
  } = useControlledDatatable('cash-breakdown-coins', {
    isOpen: true,
    primaryKey: 'denomination',
    processRow,
    initialData: coinsInitialData.map((item) => ({
      ...item,
      subTotal:
        dataTable.coins.find((card) => card.denomination === item.denomination)
          ?.subTotal || 0,
      pieces:
        dataTable.coins.find((card) => card.denomination === item.denomination)
          ?.pieces || 0,
    })) as ICBBillsCoins[],
    defaultClassName: true,
  });

  useEffect(() => {
    setDataTable((prev) => ({ ...prev, coins: datatableData }));
  }, [datatableData]);

  useEffect(() => {
    const cashTotal =
      dataTable.bills.reduce((sum, item) => sum + item.subTotal, 0) +
      dataTable.coins.reduce((sum, item) => sum + item.subTotal, 0);

    setTotal((prev) => ({
      ...prev,
      cashTotal,
      coinsTotal: dataTable.coins.reduce((sum, item) => sum + item.subTotal, 0),
    }));
  }, [dataTable]);

  return (
    <Stack
      sx={{
        flex: 1,
        px: 1,
        '& .MuiDataGrid-columnHeaders': {
          maxHeight: '36px!important',
          minHeight: '36px!important',
        },
      }}
    >
      <DataTable
        // CONFIG
        getRowId={(row) => row.referenceID}
        rows={datatableData}
        columns={BillsCoinsColumns}
        pageSize={100}
        autoPageSize={false}
        checkboxSelection={false}
        tableStyle={{ p: 0 }}
        disableAdditionalLoading
        disableColumnFilter
        disableSearch
        fullHeight
        hideFooter
        disableColumnMenu
        disableColumnSelector
        {...rowEdit}
      />
    </Stack>
  );
};
// #endregion

// #region CARD
interface ICBCard {
  referenceID: string;
  cardType: string;
  subTotal: number;
  type: string;
}

const CardColumns: GridColDef[] = [
  {
    field: 'cardType',
    headerName: 'Card Type',
    minWidth: 100,
    flex: 1,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => {
      const showIcon =
        value !== 'Cheques' &&
        value !== 'Gift Certificates' &&
        value !== 'Vouchers' &&
        value !== 'Others' &&
        value !== 'Other Card' &&
        value !== 'Other E-Payment';
      return (
        <Stack
          direction='row'
          gap={1}
          alignItems='center'
          pl={showIcon ? 0 : 1}
        >
          {showIcon && (
            <SvgIcon
              component={
                value === 'American Express'
                  ? AmericanExpressIcon
                  : value === 'Discover'
                    ? DiscoverIcon
                    : value === 'HSBC'
                      ? HSBCIcon
                      : value === 'JCB'
                        ? JCBIcon
                        : value === 'Master Card'
                          ? MasterCardIcon
                          : value === 'Visa'
                            ? VisaCardIcon
                            : value === 'Apple Pay'
                              ? ApplePayIcon
                              : value === 'GCash'
                                ? GCashIcon
                                : value === 'Google Pay'
                                  ? GooglePayIcon
                                  : value === 'Maya'
                                    ? MayaIcon
                                    : value === 'PayPal'
                                      ? PayPalIcon
                                      : PayPalIcon
              }
              sx={{ fontSize: '37px', color: 'transparent' }}
            />
          )}
          {value}
        </Stack>
      );
    },
  },
  {
    field: 'subTotal',
    headerName: 'Sub Total',
    minWidth: 100,
    editable: true,
    cellClassName: 'editable',
    flex: 1,
    type: 'number',
    renderEditCell: (params) => {
      return (
        <RenderTextFieldEditInputCell
          {...params}
          sx={{
            '& .MuiInputBase-input': { textAlign: 'right' },
            'input::-webkit-inner-spin-button': { ml: '10px!important' },
          }}
          type='number'
          inputProps={{ min: 0 }}
          onFocus={() => {
            if (params.value === 0) {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: undefined,
              });
            }
          }}
          onBlur={(event) => {
            if (!event.target.value) {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: 0,
              });
            }
          }}
          onKeyDown={(event) => {
            const input = event.target as HTMLInputElement;
            if (event.key === 'Enter' && !input.value) {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: 0,
              });
            }
          }}
        />
      );
    },
    align: 'right',
    headerAlign: 'right',
    hideSortIcons: true,
    sortable: false,
  },
];

const cardInitialData: ICBCard[] = [
  { referenceID: '1', cardType: 'American Express', subTotal: 0, type: 'card' },
  { referenceID: '2', cardType: 'Discover', subTotal: 0, type: 'card' },
  { referenceID: '3', cardType: 'HSBC', subTotal: 0, type: 'card' },
  { referenceID: '4', cardType: 'JCB', subTotal: 0, type: 'card' },
  { referenceID: '5', cardType: 'Master Card', subTotal: 0, type: 'card' },
  { referenceID: '6', cardType: 'Visa', subTotal: 0, type: 'card' },
  { referenceID: '7', cardType: 'Other Card', subTotal: 0, type: 'card' },
];

const ePaymentInitialData: ICBCard[] = [
  { referenceID: '8', cardType: 'Apple Pay', subTotal: 0, type: 'ePayment' },
  { referenceID: '9', cardType: 'GCash', subTotal: 0, type: 'ePayment' },
  { referenceID: '10', cardType: 'Google Pay', subTotal: 0, type: 'ePayment' },
  { referenceID: '11', cardType: 'Maya', subTotal: 0, type: 'ePayment' },
  { referenceID: '12', cardType: 'Paypal', subTotal: 0, type: 'ePayment' },
  {
    referenceID: '13',
    cardType: 'Other E-Payment',
    subTotal: 0,
    type: 'ePayment',
  },
];

const othersInitialData: ICBCard[] = [
  { referenceID: '14', cardType: 'Cheques', subTotal: 0, type: 'others' },
  {
    referenceID: '15',
    cardType: 'Gift Certificates',
    subTotal: 0,
    type: 'others',
  },
  { referenceID: '16', cardType: 'Vouchers', subTotal: 0, type: 'others' },
  { referenceID: '17', cardType: 'Others', subTotal: 0, type: 'others' },
];

const Card = ({ type }: { type: 'card' | 'ePayment' | 'others' }) => {
  const { dataTableState, totalState } = useFoCashBreakdownCtx();
  const [dataTable, setDataTable] = dataTableState;
  const [_, setTotal] = totalState;
  const dialogDisclosure = useDisclosure();

  const processRow = (row: ICBCard) => {
    return { ...row };
  };

  const initialData = useMemo(() => {
    const data =
      type === 'card'
        ? cardInitialData
        : type === 'ePayment'
          ? ePaymentInitialData
          : othersInitialData;
    return data.map((item) => ({
      ...item,
      subTotal:
        dataTable[type].find((entry) => entry.cardType === item.cardType)
          ?.subTotal || 0,
    })) as ICBCard[];
  }, [dataTable, type]);

  const {
    // HANDLE EDIT
    datatableData,
    setDatatableData,
    resetRowModesToView,
    rowEdit,
  } = useControlledDatatable(`${type}-breakdown-card`, {
    isOpen: true,
    primaryKey: 'cardType',
    processRow,
    initialData,
    defaultClassName: true,
  });

  const dataTableKey = useMemo(
    () => JSON.stringify(datatableData),
    [datatableData, type],
  );

  useEffect(() => {
    setDatatableData(initialData);
    return () => {
      resetRowModesToView(true);
    };
  }, [type]);

  useEffect(() => {
    if (datatableData?.[0]?.type !== type) return;
    setDataTable((prev) => ({ ...prev, [type]: datatableData }));
  }, [datatableData, type]);

  useEffect(() => {
    const total = dataTable[type].reduce((sum, item) => sum + item.subTotal, 0);
    if (type === 'others') {
      const checkTotal =
        dataTable[type]?.find((item) => item.cardType === 'Cheques')
          ?.subTotal || 0;
      const giftChecksTotal =
        dataTable[type]?.find((item) => item.cardType === 'Gift Certificates')
          ?.subTotal || 0;
      const othersTotal = total - (checkTotal + giftChecksTotal);
      setTotal((prev) => ({
        ...prev,
        checkTotal,
        giftChecksTotal,
        [`${type}Total`]: othersTotal,
      }));
      return;
    }
    setTotal((prev) => ({ ...prev, [`${type}Total`]: total }));
  }, [dataTable, type]);

  return (
    <>
      <Stack
        sx={{
          flex: 1,
          px: 1,
          '& .MuiDataGrid-columnHeaders': {
            maxHeight: '36px!important',
            minHeight: '36px!important',
          },
        }}
      >
        <DataTable
          // CONFIG
          getRowId={(row) => row.referenceID}
          rows={datatableData}
          columns={CardColumns}
          pageSize={100}
          autoPageSize={false}
          checkboxSelection={false}
          tableStyle={{ p: 0 }}
          disableAdditionalLoading
          disableColumnFilter
          disableSearch
          fullHeight
          hideFooter
          disableColumnMenu
          disableColumnSelector
          {...rowEdit}
          key={dataTableKey + type}
        />
      </Stack>
      <ConfirmationDialog
        open={dialogDisclosure.isOpen}
        onConfirm={async () => {}}
        onClose={dialogDisclosure.onClose}
      />
    </>
  );
};
// #endregion

const FOCashBreakdownDrawer = ({
  drawerDisclosure,
  transactionType,
}: {
  drawerDisclosure?: UseDisclosureInterface;
  transactionType?: 'end-shift' | 'declare-new-day';
}) => {
  const {
    //  startLoadingStatus,
    stopLoadingStatus,
  } = useAppContext();

  const dispatch = useAppDispatch();
  const pdfDisclosure = useDisclosure();
  const user = useUser();
  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty.value,
  );
  const isEndShift = useMemo(
    () => transactionType === 'end-shift',
    [transactionType],
  );
  const isDND = useMemo(
    () => transactionType === 'declare-new-day',
    [transactionType],
  );
  const printerID = useAppSelector(selectPOSPrinter) || '';
  const stationID = useAppSelector(selectPOSstationID);
  const shiftID = useAppSelector(selectPOSShiftID) || '';
  const subModuleCode = useAppSelector(selectPOSsubModuleCode);
  const pathname = window.location.pathname?.split('/')?.[1];
  const currentSalesDate = useAppSelector((state) =>
    selectSalesDateByPath(state, pathname),
  );
  const currentModuleCode = useCurrentModule();

  const { white } = getColor();
  const { toast } = useToast();
  const [refId, setRefId] = useState('');
  const [activeTransaction, setActiveTransaction] = useState<string>('Bills');
  const [remarks, setRemarks] = useState('');
  const dataTableState = useState<IDataTable>({
    bills: [],
    coins: [],
    card: [],
    ePayment: [],
    others: [],
  });
  const [dataTable, setDataTable] = dataTableState;

  const totalState = useState<ITotal>({
    cashTotal: 0,
    billsTotal: 0,
    coinsTotal: 0,
    cardTotal: 0,
    checkTotal: 0,
    giftChecksTotal: 0,
    ePaymentTotal: 0,
    othersTotal: 0,
    grandTotal: 0,
  });

  const [total, setTotal] = totalState;

  const calculateGrandTotal = () => {
    return (
      total.billsTotal +
      total.coinsTotal +
      total.cardTotal +
      total.checkTotal +
      total.giftChecksTotal +
      total.ePaymentTotal +
      total.othersTotal
    );
  };

  // #region API
  const [postCashBreakdown, { isLoading: isPosting }] =
    usePostCashBreakdownMutation();

  const [putEndShift, { isLoading: isLoadingEndShift }] = usePutEndShift();

  const [postPOSDeclareNewDay, { isLoading: isLoadingDND }] =
    usePostPOSDeclareNewDay();

  const isLoading = isPosting || isLoadingEndShift || isLoadingDND;

  const cleanState = () => {
    dispatch(clearPOSStation());
    dispatch(clearPOSMasterlist());
    dispatch(clearPOSTableArea());
  };
  // #endregion

  const navigate = useNavigate();

  const goBack = () => {
    if (window.history?.state?.idx !== 0) {
      navigate(-1);
    } else {
      navigate(`/${window.location.pathname?.split('/')?.[1] || ''}`);
    }
  };

  const memoizedTotalValue = useMemo(
    () => ({ dataTableState, totalState }),
    [total],
  );

  const memoizedDataTableValue = useMemo(
    () => ({ dataTableState, totalState }),
    [dataTable],
  );

  const allZero = Object.values(total).every((value) => value === 0);

  const handleDrawerClose = async () => {
    if (!drawerDisclosure) {
      goBack();
    } else {
      drawerDisclosure.onClose();
    }
    setActiveTransaction('Bills');
    setDataTable({ bills: [], coins: [], card: [], ePayment: [], others: [] });
    setTotal({
      cashTotal: 0,
      billsTotal: 0,
      coinsTotal: 0,
      cardTotal: 0,
      checkTotal: 0,
      giftChecksTotal: 0,
      ePaymentTotal: 0,
      othersTotal: 0,
      grandTotal: 0,
    });
  };

  const handleEndShift = async () => {
    await putEndShift({
      shiftID,
      payload: {
        properties: [
          {
            property: {
              propertyID: selectedProperty,
              shift: {
                stationID,
                shiftID,
                signature: '',
              },
            },
          },
        ],
      },
      stationID,
    })
      .unwrap()
      .then(async () => {
        try {
          // startLoadingStatus('Printing X Reading');

          if (pathname === 'point-of-sale') {
            toast({
              severity: 'success',
              message: 'Shift ended successfully',
            });
          } else {
            toast({
              severity: 'success',
              message: 'X Reading Report printed successfully',
            });
          }
          !isDND && cleanState();
        } catch (error) {
          toast({ severity: 'warning', message: error });
        } finally {
          stopLoadingStatus();
        }
      })
      .catch((err) => {
        if (err?.errMsg?.includes('Shift Already Ended')) {
          cleanState();
        }
        toast({ severity: 'error', message: err });
      })
      .finally(() => {
        stopLoadingStatus();
      });
  };

  const handleDND = async () => {
    await postPOSDeclareNewDay({
      properties: [
        {
          property: {
            propertyID: selectedProperty,
            data: {
              salesDate: currentSalesDate,
              moduleCodeSub: subModuleCode,
              printerID: printerID,
              signature: hashSignature(
                [
                  selectedProperty,
                  currentSalesDate,
                  subModuleCode,
                  printerID,
                ].join(''),
              ),
            },
          },
        },
      ],
    })
      .unwrap()
      .then(() => {
        if (pathname === 'point-of-sale') {
          toast({
            severity: 'success',
            message: 'Declare new day successfully',
          });
          toast({
            severity: 'success',
            message: 'X Reading Report printed successfully',
          });
          toast({
            severity: 'success',
            message: 'Z Reading Report printed successfully',
          });
        } else {
          toast({
            severity: 'success',
            message: 'Declare new day successfully',
          });
        }
      })
      .catch((err) => {
        toast({ severity: 'error', message: err });
      })
      .finally(() => {
        cleanState();
        stopLoadingStatus();
      });
  };

  const handleSubmit = async () => {
    const [dataTable] = dataTableState;

    const cashBreakdownObject: ICashBreakdownObject = {
      Bills: {
        totalBill1000:
          dataTable.bills.find((b) => b.denomination === 1000)?.pieces || 0,
        totalBill500:
          dataTable.bills.find((b) => b.denomination === 500)?.pieces || 0,
        totalBill200:
          dataTable.bills.find((b) => b.denomination === 200)?.pieces || 0,
        totalBill100:
          dataTable.bills.find((b) => b.denomination === 100)?.pieces || 0,
        totalBill50:
          dataTable.bills.find((b) => b.denomination === 50)?.pieces || 0,
        totalBill20:
          dataTable.bills.find((b) => b.denomination === 20)?.pieces || 0,
      },
      Coins: {
        totalCoins20:
          dataTable.coins.find((c) => c.denomination === 20)?.pieces || 0,
        totalCoins10:
          dataTable.coins.find((c) => c.denomination === 10)?.pieces || 0,
        totalCoins5:
          dataTable.coins.find((c) => c.denomination === 5)?.pieces || 0,
        totalCoins1:
          dataTable.coins.find((c) => c.denomination === 1)?.pieces || 0,
        totalCoins25c:
          dataTable.coins.find((c) => c.denomination === 0.25)?.pieces || 0,
        totalCoins10c:
          dataTable.coins.find((c) => c.denomination === 0.1)?.pieces || 0,
        totalCoins05c:
          dataTable.coins.find((c) => c.denomination === 0.05)?.pieces || 0,
        totalCoins01c:
          dataTable.coins.find((c) => c.denomination === 0.01)?.pieces || 0,
      },
      Cards: {
        totalCardMastercard:
          dataTable.card.find((c) => c.cardType === 'Master Card')?.subTotal ||
          0,
        totalCardVisa:
          dataTable.card.find((c) => c.cardType === 'Visa')?.subTotal || 0,
        totalCardJCB:
          dataTable.card.find((c) => c.cardType === 'JCB')?.subTotal || 0,
        totalCardHSBC:
          dataTable.card.find((c) => c.cardType === 'HSBC')?.subTotal || 0,
        totalCardDiscover:
          dataTable.card.find((c) => c.cardType === 'Discover')?.subTotal || 0,
        totalCardAmEx:
          dataTable.card.find((c) => c.cardType === 'American Express')
            ?.subTotal || 0,
        totalCardOthers: dataTable.card
          .filter(
            (c) =>
              ![
                'Master Card',
                'Visa',
                'JCB',
                'HSBC',
                'Discover',
                'American Express',
              ].includes(c.cardType),
          )
          .reduce((sum, c) => sum + c.subTotal, 0),
      },
      EPayments: {
        totalEPaymentPaypal:
          dataTable.ePayment.find((c) => c.cardType === 'Paypal')?.subTotal ||
          0,
        totalEPaymentGCash:
          dataTable.ePayment.find((c) => c.cardType === 'GCash')?.subTotal || 0,
        totalEPaymentMaya:
          dataTable.ePayment.find((c) => c.cardType === 'Maya')?.subTotal || 0,
        totalEPaymentApple:
          dataTable.ePayment.find((c) => c.cardType === 'Apple Pay')
            ?.subTotal || 0,
        totalEPaymentGoogle:
          dataTable.ePayment.find((c) => c.cardType === 'Google Pay')
            ?.subTotal || 0,
        totalEPaymentOthers: dataTable.ePayment
          .filter(
            (c) =>
              !['Paypal', 'GCash', 'Maya', 'Apple Pay', 'Google Pay'].includes(
                c.cardType,
              ),
          )
          .reduce((sum, c) => sum + c.subTotal, 0),
      },
      Others: {
        totalOthersCheques: total.checkTotal || 0,
        totalOthersGiftCertificate: total.giftChecksTotal || 0,
        totalOthersVouchers:
          dataTable.others.find((c) => c.cardType === 'Vouchers')?.subTotal ||
          0,
        totalOthersOthers:
          total.othersTotal -
            (dataTable.others.find((c) => c.cardType === 'Vouchers')
              ?.subTotal || 0) || 0,
      },
    };

    const signature = SHA512(
      `${selectedProperty}${user.userID}${JSON.stringify([
        { cashBreakdown: cashBreakdownObject },
      ])}`,
    ).toString();

    const cashBreakdownData: ICashBreakdown = {
      properties: [
        {
          property: {
            propertyID: selectedProperty,
            userID: user.userID,
            shiftID: currentModuleCode === 'POS' ? shiftID : '',
            cashBreakdowns: [{ cashBreakdown: cashBreakdownObject }],
            signature,
          },
        },
      ],
    };

    await postCashBreakdown(cashBreakdownData)
      .unwrap()
      .then(async (res) => {
        !isEndShift && !isDND && setRefId(res);

        toast({
          severity: 'success',
          message: 'Cash breakdown submitted successfully',
        });

        pdfDisclosure.onOpen();

        if (isEndShift) {
          await handleEndShift();
        }

        if (isDND) {
          if (currentModuleCode === 'POS') {
            await handleEndShift();
          }
          await handleDND();
        }
      })
      .catch((err) => {
        toast({ severity: 'error', message: err });
      })
      .finally(() => {
        stopLoadingStatus();
      });
  };

  return (
    <>
      <DrawerWrapper
        {...{
          open: drawerDisclosure ? drawerDisclosure.isOpen : true,
          onClose: isLoading ? () => {} : handleDrawerClose,
          withHeader: {
            title: 'Cash Breakdown',
            learnMore: () => {},
          },
        }}
      >
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            width: { xs: '100%', md: '900px' },
          }}
        >
          {/* MAIN */}
          <Stack direction='row' sx={{ flex: 1, minHeight: 0 }}>
            {/* COLUMN 1 */}
            <Stack
              sx={{
                minWidth: '180px',
                maxWidth: '300px',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
              }}
            >
              {/* TRANSACTION CARDS */}
              {useMemo(() => {
                return transactions.map((item, index) => {
                  const title =
                    item.title === 'Credit/Debit Card'
                      ? 'card'
                      : item.title === 'E-Payment'
                        ? 'ePayment'
                        : item.title.toLowerCase();

                  const data = dataTable[title];

                  const totalSubTotal = Array.isArray(data)
                    ? data.reduce(
                        (sum, itemValue) =>
                          itemValue?.type === title && sum + itemValue.subTotal,
                        0,
                      )
                    : 0;

                  return (
                    <TransactionCard
                      key={item.title + index}
                      {...{
                        ...item,
                        amount: totalSubTotal,
                        isActive: activeTransaction === item.title,
                        onClick: (item) =>
                          setTimeout(() => setActiveTransaction(item), 10), // add delay to trigger onblur set value on data table
                      }}
                    />
                  );
                });
              }, [activeTransaction, dataTable])}

              <Divider />

              {/* TOTALS */}
              <CtxProvider value={memoizedTotalValue}>
                {totalCards.map((item, index) => {
                  return (
                    <TotalCard
                      key={item.title + index}
                      {...{
                        ...item,
                        amount: total[handleCamelCase(item.title)],
                      }}
                    />
                  );
                })}
                {/* GRAND TOTAL */}
                <Stack sx={{ py: 1, px: 2 }}>
                  <Typography sx={{ fontSize: '16px' }}>Grand Total</Typography>
                  <Typography
                    sx={{
                      textAlign: 'right',
                      fontSize: '20px',
                      fontWeight: 600,
                    }}
                  >
                    {formatNumber(calculateGrandTotal(), 2, 2)}
                  </Typography>
                </Stack>
              </CtxProvider>

              {/* REMARKS */}
              {useMemo(
                () => (
                  <Stack sx={{ px: 2, py: 1 }}>
                    <ControlledTextField
                      label='Remarks'
                      inputValue={remarks}
                      setInputValue={setRemarks}
                      maxLength={999}
                      showTextLength
                      multiline
                      rows={5}
                    />
                  </Stack>
                ),
                [remarks],
              )}
            </Stack>

            <Divider orientation='vertical' />

            {/* COLUMN 2 */}
            <CtxProvider value={memoizedDataTableValue}>
              <Stack
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                }}
              >
                {activeTransaction === 'Bills' ? (
                  <Bills />
                ) : activeTransaction === 'Coins' ? (
                  <Coins />
                ) : (
                  <Card
                    type={
                      activeTransaction === 'Credit/Debit Card'
                        ? 'card'
                        : activeTransaction === 'E-Payment'
                          ? 'ePayment'
                          : 'others'
                    }
                  />
                )}
              </Stack>
            </CtxProvider>
          </Stack>

          {/* FORM BUTTON */}
          <Box
            sx={{
              position: 'sticky',
              bottom: '0',
              zIndex: 20,
              bgcolor: white,
              borderRight: { xs: 'none', sm: '1px solid #e0e0e0' },
            }}
          >
            <Divider sx={{ borderStyle: 'dashed' }} />

            <Stack
              sx={{ p: 3, gap: { xs: 3, sm: 1 }, justifyContent: 'flex-end' }}
              direction={{ xs: 'column', sm: 'row' }}
            >
              <Button
                variant='outlined'
                onClick={handleDrawerClose}
                disabled={isLoading}
              >
                Close
              </Button>
              <LoadingButton
                variant='contained'
                disabled={allZero}
                onClick={handleSubmit}
                loading={isLoading}
              >
                Submit
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      </DrawerWrapper>

      <CashBreakdownPDF
        {...{
          refId,
          modalDisclosure: {
            ...pdfDisclosure,
            onClose: () => {
              handleDrawerClose();
            },
          },
        }}
      />
    </>
  );
};

export default FOCashBreakdownDrawer;
